<template>
  <b-modal
    id="sends-history-modal"
    ref="modal"
    title="Histórico de Envios"
    size="xl"
    hide-footer
    @show="onShow"
  >
    <div class="sends-history">
      <!-- Summary counts -->
      <div class="status-summary mb-3">
        <div class="summary-card">
          <div class="summary-value">{{ totalSends }}</div>
          <div class="summary-label">Total</div>
        </div>
        <div class="summary-card status-sent">
          <div class="summary-value">{{ statusCounts.sent || 0 }}</div>
          <div class="summary-label">Entregues</div>
        </div>
        <div class="summary-card status-queued">
          <div class="summary-value">{{ statusCounts.queued || 0 }}</div>
          <div class="summary-label">Na fila</div>
        </div>
        <div class="summary-card status-failed">
          <div class="summary-value">{{ statusCounts.failed || 0 }}</div>
          <div class="summary-label">Falharam</div>
        </div>
        <div class="summary-card status-bounced">
          <div class="summary-value">{{ statusCounts.bounced || 0 }}</div>
          <div class="summary-label">Bounced</div>
        </div>
        <div class="summary-card status-superseded">
          <div class="summary-value">{{ statusCounts.superseded || 0 }}</div>
          <div class="summary-label">Substituídos</div>
        </div>
      </div>

      <!-- Filters -->
      <div class="filters mb-3">
        <div class="row">
          <div class="col-md-5">
            <input
              type="text"
              class="form-control"
              v-model="search"
              placeholder="Buscar por nome ou email..."
              @input="debouncedLoad"
            />
          </div>
          <div class="col-md-4">
            <select class="form-control" v-model="statusFilter" @change="load(1)">
              <option value="">Todos os status</option>
              <option value="sent">Entregues</option>
              <option value="queued">Na fila</option>
              <option value="failed">Falharam</option>
              <option value="bounced">Bounce</option>
              <option value="superseded">Substituídos</option>
            </select>
          </div>
          <div class="col-md-3 text-right">
            <button type="button" class="btn btn-sm btn-outline-primary" @click="load(pagination.current_page)">
              <i class="bx bx-refresh"></i> Atualizar
            </button>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div v-if="loading" class="text-center py-4">
        <b-spinner variant="primary" small></b-spinner>
      </div>

      <div v-else-if="sends.length === 0" class="text-center py-4 text-muted">
        Nenhum envio registrado para este template.
      </div>

      <div v-else class="sends-table-wrapper">
        <table class="table table-sm sends-table">
          <thead>
            <tr>
              <th>Status</th>
              <th>Participante</th>
              <th>Email</th>
              <th>Enviado em</th>
              <th>Entregue em</th>
              <th>Erro</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="send in sends" :key="send.id">
              <td>
                <span class="badge" :class="'badge-' + send.status">
                  {{ statusLabel(send.status) }}
                </span>
              </td>
              <td>{{ send.participant ? send.participant.name : '—' }}</td>
              <td>{{ send.email_to || (send.participant ? send.participant.email : '—') }}</td>
              <td>
                <span v-if="send.sent_at">{{ formatDateTime(send.sent_at) }}</span>
                <span v-else class="text-muted">{{ formatDateTime(send.created_at) }}</span>
              </td>
              <td>
                <span v-if="send.delivered_at" class="text-success">
                  <i class="bx bx-check-circle"></i> {{ formatDateTime(send.delivered_at) }}
                </span>
                <span v-else class="text-muted">—</span>
              </td>
              <td>
                <i
                  v-if="send.error_message"
                  class="bx bx-error-circle text-danger"
                  v-b-tooltip.hover
                  :title="send.error_message"
                ></i>
              </td>
              <td>
                <button
                  v-if="send.status === 'failed' || send.status === 'bounced'"
                  type="button"
                  class="btn btn-sm btn-outline-primary"
                  @click="resendOne(send)"
                  :disabled="resending[send.id]"
                >
                  <i class="bx bx-refresh"></i> Reenviar
                </button>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Pagination -->
        <div class="d-flex justify-content-between align-items-center mt-3">
          <small class="text-muted">
            Página {{ pagination.current_page }} de {{ pagination.last_page }} ({{ pagination.total }} registros)
          </small>
          <div>
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary mr-2"
              :disabled="pagination.current_page <= 1"
              @click="load(pagination.current_page - 1)"
            >
              <i class="bx bx-chevron-left"></i>
            </button>
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary"
              :disabled="pagination.current_page >= pagination.last_page"
              @click="load(pagination.current_page + 1)"
            >
              <i class="bx bx-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import services from '../services'

var loadTimeout = null

export default {
  name: 'SendsHistoryModal',
  props: {
    templateId: {
      type: [Number, String],
      required: true
    }
  },
  data: function () {
    return {
      sends: [],
      loading: false,
      search: '',
      statusFilter: '',
      statusCounts: {},
      pagination: {
        current_page: 1,
        per_page: 20,
        total: 0,
        last_page: 1
      },
      resending: {}
    }
  },
  computed: {
    totalSends: function () {
      var total = 0
      for (var k in this.statusCounts) {
        total += this.statusCounts[k]
      }
      return total
    }
  },
  methods: {
    onShow: function () {
      this.load(1)
    },
    debouncedLoad: function () {
      var self = this
      if (loadTimeout) clearTimeout(loadTimeout)
      loadTimeout = setTimeout(function () {
        self.load(1)
      }, 400)
    },
    load: async function (page) {
      var self = this
      self.loading = true
      try {
        var params = { page: page || 1, limit: 20 }
        if (self.search) params.search = self.search
        if (self.statusFilter) params.status = self.statusFilter

        var res = await services.listSends(self.templateId, params)
        self.sends = res.data.data || []
        self.pagination = res.data.meta || self.pagination
        self.statusCounts = (res.data.meta && res.data.meta.status_counts) || {}
      } catch (e) {
        self.$bvToast.toast('Erro ao carregar histórico.', {
          title: 'Erro',
          variant: 'danger',
          solid: true
        })
      } finally {
        self.loading = false
      }
    },
    statusLabel: function (status) {
      var labels = {
        queued: 'Na fila',
        sent: 'Entregue',
        failed: 'Falhou',
        bounced: 'Bounce',
        superseded: 'Substituído'
      }
      return labels[status] || status
    },
    formatDateTime: function (iso) {
      if (!iso) return ''
      var d = new Date(iso)
      if (isNaN(d.getTime())) return iso
      var dd = String(d.getDate()).padStart(2, '0')
      var mm = String(d.getMonth() + 1).padStart(2, '0')
      var yyyy = d.getFullYear()
      var hh = String(d.getHours()).padStart(2, '0')
      var mi = String(d.getMinutes()).padStart(2, '0')
      return dd + '/' + mm + '/' + yyyy + ' ' + hh + ':' + mi
    },
    resendOne: async function (send) {
      var self = this
      self.$set(self.resending, send.id, true)
      try {
        // Reusa o endpoint de resend-failed filtrando por participante específico
        // seria ideal — mas pra simplificar, usamos um payload direto via generate.
        await services.generate(self.templateId, {
          mode: 'email',
          participant_ids: [send.participant_id]
        })
        self.$bvToast.toast('Reenvio enfileirado.', {
          title: 'Sucesso',
          variant: 'success',
          solid: true
        })
        self.load(self.pagination.current_page)
      } catch (e) {
        var msg = (e.response && e.response.data && e.response.data.message) || e.message
        self.$bvToast.toast('Erro ao reenviar: ' + msg, {
          title: 'Erro',
          variant: 'danger',
          solid: true
        })
      } finally {
        self.$set(self.resending, send.id, false)
      }
    }
  }
}
</script>

<style scoped>
.status-summary {
  display: flex;
  gap: 10px;
}

.summary-card {
  flex: 1;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 6px;
  text-align: center;
  border: 1px solid #e9ecef;
}

.summary-card.status-sent { background: #e8f5e9; border-color: #c8e6c9; }
.summary-card.status-queued { background: #fff8e1; border-color: #ffe082; }
.summary-card.status-failed { background: #ffebee; border-color: #ffcdd2; }
.summary-card.status-bounced { background: #fff3e0; border-color: #ffcc80; }
.summary-card.status-superseded { background: #f3e5f5; border-color: #e1bee7; }

.summary-value {
  font-size: 22px;
  font-weight: 700;
  color: #344054;
}

.summary-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6c757d;
  margin-top: 2px;
}

.sends-table {
  font-size: 13px;
}

.sends-table th {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #475569;
  background: #f8f9fa;
  border-bottom: 2px solid #e9ecef;
}

.sends-table td {
  vertical-align: middle;
}

.badge {
  padding: 4px 8px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 4px;
}

.badge-sent {
  background: #e8f5e9;
  color: #2e7d32;
}

.badge-queued {
  background: #fff8e1;
  color: #b26a00;
}

.badge-failed,
.badge-bounced {
  background: #ffebee;
  color: #c62828;
}

.badge-superseded {
  background: #f3e5f5;
  color: #7b1fa2;
}
</style>
